// src/components/Profile/AccreditationsTab.jsx
import {
  Card,
  Col,
  Row,
  Empty,
  Tag,
  Typography,
  Badge,
  Space,
  Select,
  Input,
} from "antd";
import {
  CalendarOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  SortAscendingOutlined,
} from "@ant-design/icons";
import { useMemo, useState } from "react";
import moment from "moment";

const { Text } = Typography;
const { Option } = Select;

const fmt = (d) => (d ? moment(d).format("DD MMM YYYY") : "—");
const safe = (s) => (s || "").toString().trim().toLowerCase();

const statusTagColor = (status = "") => {
  const s = safe(status);
  if (s.includes("full") || s.includes("active")) return "green";
  if (s.includes("provisional") || s.includes("candidate")) return "blue";
  if (s.includes("suspended")) return "volcano";
  if (s.includes("expired")) return "red";
  return "gold";
};

const expiryMeta = (expiryDate) => {
  if (!expiryDate)
    return {
      state: "unknown",
      daysLeft: null,
      color: "default",
      label: "No expiry",
    };
  const days = moment(expiryDate)
    .startOf("day")
    .diff(moment().startOf("day"), "days");
  if (days < 0)
    return { state: "expired", daysLeft: days, color: "red", label: "Expired" };
  if (days <= 60)
    return {
      state: "soon",
      daysLeft: days,
      color: "orange",
      label: `${days} days left`,
    };
  return {
    state: "active",
    daysLeft: days,
    color: "green",
    label: `${days} days left`,
  };
};

const AccreditationsTab = ({ institution }) => {
  const { accreditations = [] } = institution || {};

  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sortKey, setSortKey] = useState("expiry"); // 'expiry' | 'award' | 'name'
  const [sortOrder, setSortOrder] = useState("asc"); // 'asc' | 'desc'

  const statusOptions = useMemo(() => {
    const set = new Set(
      accreditations.map((a) => a.membership_status).filter(Boolean)
    );
    return ["All", ...Array.from(set)];
  }, [accreditations]);

  const filtered = useMemo(() => {
    let list = [...accreditations];

    // search
    if (q.trim() !== "") {
      const needle = safe(q);
      list = list.filter(
        (a) =>
          safe(a.name).includes(needle) ||
          safe(a.membership_status).includes(needle)
      );
    }

    // status filter
    if (statusFilter !== "All") {
      list = list.filter((a) => a.membership_status === statusFilter);
    }

    // sort
    list.sort((a, b) => {
      const dir = sortOrder === "asc" ? 1 : -1;

      if (sortKey === "name") {
        return safe(a.name).localeCompare(safe(b.name)) * dir;
      }

      if (sortKey === "award") {
        const av = a.date_awarded ? moment(a.date_awarded).valueOf() : 0;
        const bv = b.date_awarded ? moment(b.date_awarded).valueOf() : 0;
        return (av - bv) * dir;
      }

      // default: expiry
      const av = a.expiry_date
        ? moment(a.expiry_date).valueOf()
        : Number.MAX_SAFE_INTEGER;
      const bv = b.expiry_date
        ? moment(b.expiry_date).valueOf()
        : Number.MAX_SAFE_INTEGER;
      return (av - bv) * dir;
    });

    return list;
  }, [accreditations, q, statusFilter, sortKey, sortOrder]);

  if (accreditations.length === 0) {
    return <Empty description="No accreditations found." />;
  }

  return (
    <div className="pt-5 pb-4">
      {/* Controls */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <Space wrap>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search accreditations"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ width: 260 }}
          />
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 220 }}
          >
            {statusOptions.map((s) => (
              <Option key={s} value={s}>
                {s}
              </Option>
            ))}
          </Select>
        </Space>

        <Space wrap>
          <Select
            value={sortKey}
            onChange={setSortKey}
            style={{ width: 180 }}
            suffixIcon={<SortAscendingOutlined />}
          >
            <Option value="expiry">Sort by Expiry</Option>
            <Option value="award">Sort by Awarded</Option>
            <Option value="name">Sort by Name</Option>
          </Select>
          <Select
            value={sortOrder}
            onChange={setSortOrder}
            style={{ width: 140 }}
          >
            <Option value="asc">Asc</Option>
            <Option value="desc">Desc</Option>
          </Select>
        </Space>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <Empty description="No accreditations match your filters." />
      ) : (
        <Row gutter={[16, 16]}>
          {filtered.map((item) => {
            const exp = expiryMeta(item.expiry_date);

            return (
              <Col key={item.id} xs={24} sm={12} md={6}>
                <Card
                  title={
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-gray-800">
                        {item.name || "—"}
                      </span>
                      <Tag color={statusTagColor(item.membership_status)}>
                        {item.membership_status || "Status —"}
                      </Tag>
                    </div>
                  }
                  className="border border-gray-200!"
                >
                  <div className="space-y-3 text-gray-700 text-sm">
                    {/* Awarded */}
                    <div className="flex items-start gap-2">
                      <CheckCircleOutlined className="text-green-600 mt-[3px]" />
                      <div>
                        <Text strong>Date Awarded:</Text>{" "}
                        <Text>{fmt(item.date_awarded)}</Text>
                      </div>
                    </div>

                    {/* Expiry */}
                    <div className="flex items-start gap-2">
                      {exp.state === "expired" ? (
                        <CloseCircleOutlined className="text-red-500 mt-[3px]" />
                      ) : exp.state === "soon" ? (
                        <ExclamationCircleOutlined className="text-orange-500 mt-[3px]" />
                      ) : (
                        <CalendarOutlined className="text-purple-500 mt-[3px]" />
                      )}
                      <div>
                        <Text strong>Expiry Date:</Text>{" "}
                        <Text>{fmt(item.expiry_date)}</Text>
                        {item.expiry_date && (
                          <div className="text-xs text-gray-500">
                            {moment(item.expiry_date).fromNow()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
    </div>
  );
};

export default AccreditationsTab;
